
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { detectSpecialAdminEmails } from '@/utils/adminUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSubscription?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false, 
  requireSubscription = false
}) => {
  const location = useLocation();
  const { isAuthenticated, user, loading } = useAuth();
  
  const currentPath = location.pathname || '';
  const searchParams = new URLSearchParams(location.search);
  const paymentSuccess = searchParams.get('payment_success');
  const sessionId = searchParams.get('session_id');
  
  // Public pages - always allow (including update-password for password recovery)
  const isPublicPage = currentPath === '/login' || 
                        currentPath === '/registro' || 
                        currentPath === '/reset-password' || 
                        currentPath === '/password-reset' || 
                        currentPath === '/update-password' ||
                        currentPath === '/';
  
  if (isPublicPage) {
    return <>{children}</>;
  }

  // Still loading auth - show loading content briefly
  if (loading) {
    console.log('🔍 [ProtectedRoute] Auth is still loading');
    return <>{children}</>;
  }

  // Special case: If user comes back from successful payment but is not authenticated,
  // redirect to registration page instead of login
  if (!isAuthenticated && paymentSuccess === 'true' && sessionId) {
    const registrationUrl = `/registro?payment_success=true&session_id=${sessionId}&email=${searchParams.get('email') || ''}`;
    return <Navigate to={registrationUrl} replace />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    console.log('🔍 [ProtectedRoute] User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Check admin requirement
  if (requireAdmin) {
    console.log('🔍 [ProtectedRoute] Admin access required for path:', currentPath);
    console.log('🔍 [ProtectedRoute] User email:', user?.email);
    console.log('🔍 [ProtectedRoute] User ID:', user?.id);
    console.log('🔍 [ProtectedRoute] User metadata role:', (user as any)?.user_metadata?.role);
    console.log('🔍 [ProtectedRoute] User role:', user?.role);
    
    const isSpecialAdmin = user?.email ? detectSpecialAdminEmails(user.email) : false;
    console.log('🔍 [ProtectedRoute] Is special admin:', isSpecialAdmin);
    
    const isAdmin = (user as any)?.user_metadata?.role === 'admin' || 
                    user?.role === 'admin' ||
                    isSpecialAdmin;
    
    console.log('🔍 [ProtectedRoute] Final admin check result:', isAdmin);
    
    if (!isAdmin) {
      console.log('❌ [ProtectedRoute] Admin access DENIED for:', user?.email, {
        userMetadataRole: (user as any)?.user_metadata?.role,
        userRole: user?.role,
        isSpecialAdmin: isSpecialAdmin,
        currentPath: currentPath
      });
      return <Navigate to="/unauthorized" replace />;
    } else {
      console.log('✅ [ProtectedRoute] Admin access GRANTED for:', user?.email, 'to path:', currentPath);
    }
  }

  // Allow access for authenticated users
  console.log('🔍 [ProtectedRoute] Access granted to:', currentPath);
  return <>{children}</>;
};

export default ProtectedRoute;
